import {
  Button,
  Checkbox,
  ContextToolbar,
  Input,
  Modal,
  PanelPermissions,
  useTraversal,
} from '@guillotinaweb/react-gmi'
import { Affix, Dropdown, Menu, Select } from 'antd'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import AceEditor from 'react-ace'
import uuid from 'uuid'
import { AuditLogTab } from '../../../common/auditlog'
import { DatePicker } from '../../../common/date'
import { DnD } from '../../../common/dnd'
import { I18NField } from '../../../components/i18nfield'
import { CustomPreview } from '../../../components/preview'
import { TabsPanelWithLock } from '../../../components/tab-panel-with-lock'
import { LANGS, defaultHomeSections, homeSectionsToAdd } from '../../../consts'
import { formatStartEnd, isSameDay } from '../../../utils'
import { BlogPostsForm } from './blog_posts_form'
import { CatalogForm } from './catalog_form'
import { HomeSection } from './home_section'
import { ImageForm } from './image_form'
import { OfferBannerForm } from './offer_banner_form'
import { ProductBannerForm } from './product_banner_form'
import { ImagesForm } from './slider_images_form'

import 'ace-builds/src-noconflict/mode-html'
import 'ace-builds/src-noconflict/theme-textmate'

import './home_editor.css'

function TemplateData({ state }) {
  const timeout = useRef()
  const [openReset, setOpenReset] = useState(false)
  const initialContext = useRef(state.context)
  const [loading, setLoading] = useState(false)
  const Ctx = useTraversal()
  const canModifyContent = Ctx.hasPerm('guillotina.ModifyContent')
  const epoch = useRef(Date.now())
  const [start, setStart] = useState(state.context.date_start)
  const [end, setEnd] = useState(state.context.date_end)
  const [editIndx, setEditIndx] = useState()
  const [sections, setSections] = useState(
    state.context.sections.map((s) => ({ ...s, key: uuid() }))
  )
  const selected = sections[editIndx] || {}
  const close = () => setEditIndx()

  function edit(key, value) {
    clearTimeout(timeout.current)
    timeout.current = setTimeout(() => {
      setSections((c) =>
        c.map((s, i) =>
          i === editIndx
            ? {
                ...s,
                [key]: value,
              }
            : s
        )
      )
    }, 150)
  }

  function onSavePreview(site) {
    const [, previewLang] = site.split('?preview=')
    const i18n = (t) => (t ? t[previewLang] : undefined)
    const config = {
      sections: sections
        .filter(
          (s) =>
            !s.hidden &&
            (!s.for_langs?.length || s.for_langs.includes(previewLang))
        )
        .map((s) => ({
          ...s,
          title: i18n(s.title),
          html: encodeURIComponent(s.html?.[previewLang]),
          buttonText: i18n(s.buttonText),
          buttonLink: Object.entries(s.buttonLink || {}).reduce(
            (o, [lang, v]) => {
              o[lang] = encodeURIComponent(v)
              return o
            },
            {}
          ),
        })),
    }
    window.localStorage.setItem('preview', JSON.stringify(config))
  }

  function reset(newSections) {
    const {
      sections: lastSections,
      date_start,
      date_end,
    } = initialContext.current
    epoch.current = Date.now()
    setSections(newSections || lastSections)
    setStart(date_start)
    setEnd(date_end)
    setOpenReset(false)
  }

  async function save() {
    const [date_start, date_end] = formatStartEnd(start, end)

    if (new Date(date_start) >= new Date(date_end)) {
      Ctx.flash('The end date must be greater than the start date.', 'danger')
      window.scroll({ top: 0 })
      return
    }

    if (sections.some((s) => s.id === 'product-banner' && !s.reference)) {
      Ctx.flash(
        "It's mandatory to put a product in the product-banner section.",
        'danger'
      )
      window.scroll({ top: 0 })
      return
    }

    const startDate = new Date(start)
    const { format } = new Intl.DateTimeFormat('es-ES')
    const message = isSameDay(startDate, new Date())
      ? 'The changes will be applied in approximately 12 minutes.'
      : `Changes will be applied on ${format(startDate)}`

    const flashOk = [`Home stored correctly. ${message}`, 'success']
    const flashKo = ['Error updating home page', 'danger']
    const home = { sections, date_start, date_end }
    setLoading(true)
    const { status } = await Ctx.client.patch(Ctx.path, home)
    Ctx.flash(...(status === 204 ? flashOk : flashKo))
    Ctx.refresh()
    window.scroll({ top: 0 })
    setLoading(false)
  }

  function addCustomSection({ key }) {
    const newSection = {
      ...homeSectionsToAdd.find((s) => s.id === key),
      key: uuid(),
    }
    if (newSection) setSections([newSection, ...sections])
    window.scroll({ top: 200 })
  }

  useEffect(() => () => clearTimeout(timeout.current), [])

  return (
    <Fragment key={epoch.current}>
      <Affix offsetTop={10}>
        <div className="home-controls">
          <DatePicker
            value={start}
            name="From (00:00h)"
            onUpdate={(e) => setStart(e.target.value)}
          />
          <DatePicker
            value={end}
            name="To (23:59h)"
            onUpdate={(e) => setEnd(e.target.value)}
          />
          <Dropdown
            overlay={
              <Menu onClick={addCustomSection}>
                {homeSectionsToAdd.map((s) => (
                  <Menu.Item key={s.id}>Section "{s.id}"</Menu.Item>
                ))}
              </Menu>
            }
          >
            <Button>Add</Button>
          </Dropdown>
          <Button onClick={() => setOpenReset(true)} className="button">
            Reset
          </Button>
          <CustomPreview
            openAllOption={false}
            domains={[
              {
                label: 'Español',
                url: `${window.location.origin}?preview=es`,
              },
              {
                label: 'English',
                url: `${window.location.origin}?preview=en`,
              },
              {
                label: 'Deutsch',
                url: `${window.location.origin}?preview=de`,
              },
              {
                label: 'Italiano',
                url: `${window.location.origin}?preview=it`,
              },
              {
                label: 'Français',
                url: `${window.location.origin}?preview=fr`,
              },
            ]}
            onBeforeClick={onSavePreview}
          />
          <Button disabled={loading || !canModifyContent} onClick={save}>
            Save and Publish
          </Button>
        </div>
      </Affix>
      <div class="home-sections-wrapper">
        <DnD
          list={sections}
          onChange={setSections}
          getChild={(section, index) => (
            <HomeSection
              custom={section}
              onEdit={() => setEditIndx(index)}
              id={section.id}
            />
          )}
        />
      </div>
      <Modal
        className="home-modal-content"
        isActive={editIndx !== undefined}
        setActive={close}
      >
        <EditHomeSection
          selected={selected}
          edit={edit}
          close={close}
          remove={() => {
            setSections(sections.filter((s, i) => i !== editIndx))
            close()
          }}
        />
      </Modal>

      <Modal
        className="home-modal-content"
        isActive={openReset}
        setActive={() => setOpenReset(false)}
      >
        <h1 className="title">Reset</h1>
        <strong>Are you sure to remove the latest changes?</strong>
        <div className="reset-buttons">
          <Button className="button" onClick={() => reset()}>
            Yes, return to the last saved state
          </Button>
          <Button className="button" onClick={() => reset(defaultHomeSections)}>
            Yes, return to the initial state
          </Button>
          <Button onClick={() => setOpenReset(false)}>
            No, no applying anything
          </Button>
        </div>
      </Modal>
    </Fragment>
  )
}

function EditHomeSection({ selected, edit, close, remove }) {
  const [toogleRemove, setToogleRemove] = useState(false)
  const langs = selected.for_langs?.length ? selected.for_langs : LANGS

  if (toogleRemove) {
    return (
      <div>
        <h1 className="title">Remove {(selected.title || {}).es}</h1>
        <p>Are you sure?</p>
        <div className="top-controls level level-right">
          <Button
            className="button"
            onClick={() => {
              remove()
              setToogleRemove(false)
            }}
          >
            Yes
          </Button>
          <Button onClick={() => setToogleRemove(false)}>No</Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <h1 className="title">Edit {(selected.title || {}).es}</h1>
      <div className="top-controls level level-right">
        <Checkbox
          key={(selected.title || {}).es}
          placeholder=" Show section"
          value={!selected.hidden}
          onChange={(v) => edit('hidden', !v)}
        />
        {homeSectionsToAdd.some((s) => s.id === selected.id) && (
          <Button
            className="is-small button"
            onClick={() => setToogleRemove(true)}
          >
            Remove section
          </Button>
        )}
      </div>
      {!selected.hidden && (
        <>
          <label className="label">Section languages</label>
          <Select
            mode="multiple"
            placeholder="Restrict this section to certain languages only (default is all languages)."
            defaultValue={selected.for_langs || []}
            key={selected.key}
            onChange={(l) => edit('for_langs', l)}
            style={{ width: '100%' }}
          >
            {LANGS.map((lang) => (
              <Select.Option key={lang}>{lang}</Select.Option>
            ))}
          </Select>
        </>
      )}
      {!selected.hidden && selected.id === 'html' && (
        <I18NField key={langs.join()} langs={langs} Widget={() => null}>
          {(lang) => (
            <Fragment key={lang}>
              <label className="label">Title</label>
              <Input
                value={selected.title?.[lang] || ''}
                onChange={(v) =>
                  edit('title', { ...selected.title, [lang]: v })
                }
              />
              <label className="label">HTML</label>
              <AceEditor
                mode="html"
                theme="textmate"
                value={selected.html?.[lang]}
                onChange={(text) =>
                  edit('html', { ...selected.html, [lang]: text })
                }
                name="HTML_editor"
                editorProps={{ $blockScrolling: true }}
              />
            </Fragment>
          )}
        </I18NField>
      )}
      {!selected.hidden && selected.id?.includes('image-slider') && (
        <ImagesForm
          selected={selected}
          edit={edit}
          langs={langs}
          id={selected.id}
        />
      )}
      {!selected.hidden &&
        selected.id?.includes('image') &&
        selected.id !== 'image-slider' && (
          <ImageForm
            selected={selected}
            edit={edit}
            langs={langs}
            id={selected.id}
          />
        )}
      {!selected.hidden && selected.id === 'product-banner' && (
        <ProductBannerForm selected={selected} edit={edit} langs={langs} />
      )}
      {!selected.hidden && selected.id === 'offer-banner' && (
        <OfferBannerForm selected={selected} edit={edit} langs={langs} />
      )}
      {!selected.hidden && selected.id === 'blog-posts' && (
        <BlogPostsForm selected={selected} edit={edit} langs={langs} />
      )}
      {!selected.hidden && selected.type && (
        <CatalogForm selected={selected} edit={edit} langs={langs} />
      )}
      <div className="level level-right">
        <Button onClick={close}>Close</Button>
      </div>
    </>
  )
}

export function HomeView(props) {
  const ctx = useTraversal()
  const calculated = ctx.filterTabs(
    {
      Data: TemplateData,
      Permissions: PanelPermissions,
      AuditLog: AuditLogTab,
    },
    {
      Data: 'guillotina.ViewContent',
      Sections: 'guillotina.ModifyContent',
    }
  )

  return (
    <TabsPanelWithLock
      tabs={calculated}
      currentTab="Data"
      editableTabs={['Data']}
      rightToolbar={<ContextToolbar {...props} />}
      {...props}
    />
  )
}
